import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { confirmDialog, EmptyState } from "../../../components/ui";
import { useAddShipmentEvent } from "../../../features/shipments/mutations";
import {
  SHIPMENT_STAGES,
  STAGE_EVENT_TYPE,
  furthestProgressionType,
  isBackwardsEvent,
  isDroppableStage,
  shipmentPosition,
  shipmentStage,
  type Shipment,
  type ShipmentStage,
} from "../../../features/shipments/types";
import { useShipmentUnit } from "../hooks/useShipmentUnit";
import { KanbanCard } from "./KanbanCard";
import {
  trackShipmentStageChanged,
  trackShipmentStageChangeAbandoned,
  trackShipmentStageChangeFailed,
} from "../../../analytics/shipmentsAnalytics";

interface KanbanViewProps {
  shipments: Shipment[];
  onCardClick: (shipment: Shipment) => void;
}

const COLUMN_STYLES = {
  pending: {
    icon: "ri-time-line",
    header: "bg-subtle",
    text: "text-content-secondary",
    border: "border-line",
    badge: "bg-app text-content-secondary",
  },
  transit: {
    icon: "ri-truck-line",
    header: "bg-info/10",
    text: "text-info",
    border: "border-info/30",
    badge: "bg-info/20 text-info",
  },
  delivered: {
    icon: "ri-check-double-line",
    header: "bg-success/10",
    text: "text-success",
    border: "border-success/30",
    badge: "bg-success/20 text-success",
  },
  incident: {
    icon: "ri-alert-line",
    header: "bg-error/10",
    text: "text-error",
    border: "border-error/30",
    badge: "bg-error/20 text-error",
  },
} as const satisfies Record<ShipmentStage, unknown>;

export function KanbanView({ shipments, onCardClick }: KanbanViewProps) {
  const { t } = useTranslation("envios");
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ShipmentStage | null>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);

  const addShipmentEvent = useAddShipmentEvent();
  const draggedShipment = shipments.find((item) => item.id === draggedId) ?? null;
  const draggedUnit = useShipmentUnit(draggedShipment?.actual_device_id ?? null);

  const handleScroll = (source: "top" | "bottom") => {
    const sourceRef = source === "top" ? topScrollRef : bottomScrollRef;
    const targetRef = source === "top" ? bottomScrollRef : topScrollRef;
    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
    }
  };

  const grouped = SHIPMENT_STAGES.reduce(
    (acc, stage) => ({
      ...acc,
      [stage]: shipments.filter((shipment) => shipmentStage(shipment) === stage),
    }),
    {} as Record<ShipmentStage, Shipment[]>,
  );

  const handleDrop = async (event: React.DragEvent, stage: ShipmentStage) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverColumn(null);
    setDraggedId(null);

    if (!draggedShipment) return;
    if (shipmentStage(draggedShipment) === stage) return;

    if (!isDroppableStage(stage)) {
      toast.error(t("kanban.notDroppable"));
      return;
    }

    const eventType = STAGE_EVENT_TYPE[stage];
    const fromStage = shipmentStage(draggedShipment);
    const backwards = isBackwardsEvent(draggedShipment, eventType);

    if (backwards) {
      const confirmed = await confirmDialog({
        title: t("detail.backwardsTitle"),
        text: t("detail.backwardsText", {
          event: t(`eventTypes.${eventType}`),
          current: t(`eventTypes.${furthestProgressionType(draggedShipment)}`),
        }),
        confirmText: t("detail.backwardsConfirm"),
        tone: "warning",
      });
      if (!confirmed) {
        // Mide cuantos retrocesos son un error de arrastre: si casi todos se
        // cancelan, el problema esta en la zona de suelta del kanban.
        trackShipmentStageChangeAbandoned({
          toStage: stage,
          method: "kanban_drag",
        });
        return;
      }
    }

    const history = shipmentPosition(draggedShipment);
    const lat = draggedUnit?.latitude ?? history?.lat ?? null;
    const long = draggedUnit?.longitude ?? history?.long ?? null;

    addShipmentEvent.mutate(
      {
        shipment: draggedShipment,
        event: {
          event_type: eventType,
          lat,
          long,
          address: draggedUnit?.address || null,
          note: null,
        },
      },
      {
        onSuccess: () => {
          // Ni coordenadas ni direccion ni cliente: solo etapas y el metodo,
          // que es lo que separa el arrastre del kanban del guardado manual.
          trackShipmentStageChanged({
            fromStage,
            toStage: stage,
            eventType,
            method: "kanban_drag",
            isBackwards: backwards,
            // El arrastre nunca adjunta archivos. Antes aqui iba "tenia
            // coordenadas", que no es evidencia y ademas sale gratis al
            // arrastrar: hacia que el kanban pareciera 100% evidenciado.
            hasEvidence: false,
          });
          toast.success(t("kanban.eventAdded", { stage: t(`stages.${stage}`) }));
        },
        onError: () => {
          trackShipmentStageChangeFailed({
            toStage: stage,
            method: "kanban_drag",
          });
          toast.error(t("detail.saveError"));
        },
      },
    );
  };

  return (
    <div className="flex flex-col">
      <div
        ref={topScrollRef}
        onScroll={() => handleScroll("top")}
        className="overflow-x-auto hidden sm:block bg-surface"
      >
        <div className="h-2 flex gap-4">
          {SHIPMENT_STAGES.map((stage) => (
            <div key={stage} className="flex-shrink-0 w-full sm:w-80" />
          ))}
        </div>
      </div>

      <div
        ref={bottomScrollRef}
        onScroll={() => handleScroll("bottom")}
        className="flex gap-4 overflow-x-auto pb-4 min-h-full mt-2"
      >
        {SHIPMENT_STAGES.map((stage) => {
          const styles = COLUMN_STYLES[stage];
          const items = grouped[stage] ?? [];

          return (
            <div
              key={stage}
              className={`flex flex-col rounded-lg transition-all flex-shrink-0 w-full sm:w-80 ${
                dragOverColumn === stage
                  ? "border-2 border-primary-500 shadow-lg shadow-primary-500/20"
                  : "border border-line"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
              }}
              onDragEnter={() => setDragOverColumn(stage)}
              onDrop={(e) => handleDrop(e, stage)}
            >
              <div
                className={`${styles.header} border-b-2 ${styles.border} p-3 flex items-center gap-2 flex-shrink-0`}
              >
                <i className={`${styles.icon} text-lg ${styles.text}`} />
                <div className="flex-1">
                  <h3 className={`font-semibold ${styles.text} text-sm`}>
                    {t(`stages.${stage}`)}
                  </h3>
                </div>
                <span
                  className={`${styles.badge} px-2.5 py-0.5 rounded-full text-xs font-bold`}
                >
                  {items.length}
                </span>
              </div>

              <div className="bg-app p-3">
                {items.length === 0 ? (
                  <EmptyState icon="ri-inbox-line" title={t("noEnvios")} />
                ) : (
                  items.map((shipment) => (
                    <div
                      key={shipment.id}
                      draggable
                      onDragStart={(e) => {
                        setDraggedId(shipment.id);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      className={`transition-opacity ${
                        draggedId === shipment.id ? "opacity-50" : "opacity-100"
                      }`}
                    >
                      <KanbanCard
                        shipment={shipment}
                        onClick={() => onCardClick(shipment)}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
